import { useContext, useState } from 'react';
import Scenario from '../../../domain/models/Scenario';
import ContactsContext from '../../contexts/data/ContactsContext';
import ScenariosContext from '../../contexts/data/ScenariosContext';
import { Badge, Button, Flex, FloatButton, Table, Tag, message } from 'antd';
import { advancedOnRow, getTagColor } from '../../../core/utils/commonUtils';
import TagsContext from '../../contexts/data/TagsContext';
import TagModel from '../../../domain/models/Tag';
import moment from 'moment';
import ContactActionModal from './modal/ContactActionModal';
import { ReloadOutlined, HourglassOutlined, CheckOutlined, DeleteOutlined, UserOutlined, PhoneOutlined, ThunderboltOutlined } from '@ant-design/icons';
import AuthenticationContext from '../../contexts/auth/AuthenticationContext';
import SubscriptionsContext from '../../contexts/data/SubscriptionsContext';
import ConfirmWarningModal from '../@shared/ConfirmWarningModal';
import { useNavigate } from 'react-router-dom';
import { useWindowSize } from '@leedpulse-monorepo/hooks';
import { getInterestLevel } from '@leedpulse-monorepo/utils';
import { ContextStatus, TagCategory } from '@leedpulse-monorepo/enums';
import { IContact } from '@leedpulse-monorepo/interfaces';
import { CONTACTS_PAGE_PATH } from '../../pages/contacts/ContactsPage';

interface ContactsListProps {
  scopedId: string | null;
  onPageChange: (page: number) => void;
}

const ContactsList: React.FC<ContactsListProps> = ({ scopedId, onPageChange }) => {
  const [contactsSelected, setContactsSelected] = useState<IContact[]>([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isContactActionModalVisible, setIsContactActionModalVisible] = useState(false);

  const contactsContext = useContext(ContactsContext);
  const scenariosContext = useContext(ScenariosContext);
  const tagsContext = useContext(TagsContext);
  const authContext = useContext(AuthenticationContext);
  const subscriptionsContext = useContext(SubscriptionsContext);
  const windowSize = useWindowSize();
  const navigate = useNavigate();

  const loading = contactsContext.status === ContextStatus.LOADING;

  const handlePaginationChange = (antPage: number) => {
    onPageChange(antPage - 1);
  };

  const handlePageSizeChange = (_current: number, newPageSize: number) => {
    contactsContext.search(contactsContext.lastQuery, 0, newPageSize);
  };

  const deselectAllRows = () => {
    setSelectedRowKeys([]);
    setContactsSelected([]);
  };

  const onUpdatedRows = () => {
    deselectAllRows();
    contactsContext.refresh();
  };

  const handleDeleteContactsBtnClick = async () => {
    const apiKey = subscriptionsContext.selected?.apiKey;
    if (scopedId && apiKey) {
      const contactIds = contactsSelected.map((c) => c.id);
      const result = await contactsContext.deleteSelected(authContext.authentication, apiKey, contactIds);
      // On failure ContactsPage shows the context error, a refresh would reset it before render
      if (result.status === ContextStatus.SUCCESS) {
        message.success('Suppression des contacts lancée, la liste sera à jour dans quelques instants');
        contactsContext.refresh();
      }
      deselectAllRows();
      setIsDeleteModalOpen(false);
    }
  };

  const scenarios: Scenario[] = scenariosContext.allScenarios;

  const columns = windowSize.width > 576
    ? [
      {
        title: <Flex align="center"><UserOutlined style={{ marginRight: 6 }} /> Nom complet</Flex>,
        render: (contact: IContact) => (
          <>
            {contact.last_activity?.role !== '' ? (
              <CheckOutlined style={{ color: '#389e0d', marginRight: '4px' }} title="Contact déjà engagé" />
            ) : (
              <HourglassOutlined style={{ color: '#ffd591', marginRight: '4px' }} title="Contact non engagé, en attente du 1er message" />
            )}
            <span>{contact.contact?.firstname} {contact.contact?.lastname}</span>
            {contact.is_archived && <Badge color="gray" style={{ marginLeft: '8px' }} count="Archivé" />}
          </>
        ),
      },
      ...(scopedId
        ? [
          {
            title: '✏️ Tags',
            render: (contact: IContact) => {
              const typologyTags: TagModel[] = tagsContext.tags.filter(
                (t) => t.category === TagCategory.typology && Object.keys(contact.tags ?? {}).includes(t.id)
              );
              return (
                <>
                  <Tag className="contact-tile__content__owner-tag" color={getTagColor(contact.qualification_ai?.qualification || 'not_available')}>
                    &#8226; {getInterestLevel(contact.qualification_ai?.qualification || 'not_available')}
                  </Tag>
                  <Tag className="contact-tile__content__owner-tag" color="lightgray">
                    {scenarios.find((s) => s.id === contact.contact?.prospect_category)?.name || contact.contact?.prospect_category}
                  </Tag>
                  {typologyTags.map((tag) => (
                    <Tag key={tag.id} color={tag.color} style={{ color: '#FFFFFF' }}>
                      {tag.name}
                    </Tag>
                  ))}
                </>
              );
            },
          },
        ]
        : []),
      {
        title: <Flex align="center"><PhoneOutlined style={{ marginRight: 6 }} /> Téléphone</Flex>,
        render: (contact: IContact) => (
          <span>{contact.contact?.phone}</span>
        ),
      },
      {
        title: <Flex align="center"><ThunderboltOutlined style={{ marginRight: 6 }} /> Dernière activité</Flex>,
        render: (contact: IContact) => (
          <span>
            {contact.last_activity?.timestamp
              ? moment(contact.last_activity.timestamp).fromNow()
              : '-'}
          </span>
        ),
      },
    ]
    : [
      {
        title: 'ID',
        dataIndex: 'id',
        key: 'id',
      },
    ];

  return (
    <>
      {contactsSelected.length > 0 && (
        <>
          <Button
            type="primary"
            icon={<ReloadOutlined />}
            style={{ marginLeft: '1rem' }}
            onClick={deselectAllRows}
          >
            Tout déselectionner
          </Button>
          <Button
            danger
            icon={<DeleteOutlined />}
            style={{ marginLeft: '1rem' }}
            onClick={() => setIsDeleteModalOpen(true)}
          >
            Supprimer
          </Button>
          <h4 style={{ marginLeft: '0.5rem', marginBottom: '1rem' }}>
            {contactsSelected.length} contact(s) sélectionné(s)
          </h4>
        </>
      )}

      <Table
        rowSelection={
          scopedId
            ? {
              type: 'checkbox',
              selectedRowKeys,
              onChange: (newSelectedRowKeys: React.Key[], selectedRows: IContact[]) => {
                setSelectedRowKeys(newSelectedRowKeys);
                setContactsSelected([...selectedRows]);
              },
              preserveSelectedRowKeys: true,
            }
            : undefined
        }
        pagination={{
          current: contactsContext.page + 1,
          total: contactsContext.contactsCount,
          pageSize: contactsContext.hitsPerPage,
          onChange: handlePaginationChange,
          onShowSizeChange: (_current, newSize) => handlePageSizeChange(_current, newSize),
          showSizeChanger: true,
            simple: true,
          pageSizeOptions: [20, 50, 100, 200],
          position: ['bottomRight', 'topRight'],
          disabled: loading,
        }}
        loading={loading}
        dataSource={contactsContext.contacts}
        columns={columns}
        rowKey={(record) => record.id}
        onRow={(record) =>
          advancedOnRow({
            url: `${CONTACTS_PAGE_PATH}/${record.id}`,
            navigate,
            className: 'table-row',
          })
        }
      />

      {contactsSelected.length > 0 && scopedId && (
        <>
          <FloatButton
            style={{ marginBottom: '1rem', marginRight: '1rem' }}
            tooltip={<div>Lancer une action</div>}
            badge={{ count: contactsSelected.length }}
            onClick={() => setIsContactActionModalVisible(true)}
          />
          <ContactActionModal
            subscriptionId={scopedId}
            scenarios={scenarios}
            selectedContacts={contactsSelected}
            visible={isContactActionModalVisible}
            onClose={() => setIsContactActionModalVisible(false)}
            onUpdated={onUpdatedRows}
          />
          <ConfirmWarningModal
            text={`Voulez-vous vraiment supprimer ${contactsSelected.length} contact(s) ?`}
            isModalOpen={isDeleteModalOpen}
            isLoading={loading}
            onOk={handleDeleteContactsBtnClick}
            onCancel={() => setIsDeleteModalOpen(false)}
          />
        </>
      )}
    </>
  );
};

export default ContactsList;
